import { Box, styled, Typography } from '@mui/material'
import {
  PickersDay,
  PickersDayProps,
  StaticDatePicker,
} from '@mui/x-date-pickers'
import React, { useEffect, useRef, useState } from 'react'
import { useSwipeable } from 'react-swipeable'
import { Icons } from 'utoniq-ui'

import { styles } from '@/components/styles'
import { dayjs } from '@/lib/dayjs'

type Props = {
  dates: string[]
  onFetch: (month: string) => Promise<void>
  month?: string
  className: string
}

// @mui/x-date-pickers アップデートで仕様が変わるとstyle指定が効かなくなる場合がある
const StyledDatePicker = styled(StaticDatePicker)({
  width: '100%',
  '& .MuiDateCalendar-root': {
    width: '100%',
    height: 'unset',
  },
  '& .MuiDateCalendar-root div': {
    width: '100%',
  },
  '& .MuiDayCalendar-weekContainer, & .MuiDayCalendar-header': {
    justifyContent: 'space-between',
    padding: '0 20px',
  },
  '& .MuiDayCalendar-header': {
    maxHeight: '20px',
    paddingLeft: '22px',
  },
  '& .MuiDayCalendar-header > *': {
    maxWidth: '24px',
  },
  '& .MuiDayCalendar-weekContainer > *': {
    maxWidth: '32px',
    minWidth: '32px',
    textAlign: 'center',
  },
  '& .MuiDayCalendar-slideTransition': {
    minHeight: '256px',
  },
  '& .MuiPickersCalendarHeader-root': {
    display: 'none',
  },
  '& .MuiDayCalendar-weekContainer > div': {
    minWidth: '32px',
  },
  '& .MuiDayCalendar-weekContainer': {
    margin: '8px 0',
  },
  '& .MuiButtonBase-root': {
    maxWidth: '32px',
    maxHeight: '32px',
  },
})

const convertToFullWeekday = (weekday: string) => {
  const weekdays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
  switch (weekday) {
    case '日':
      return weekdays[0]
    case '月':
      return weekdays[1]
    case '火':
      return weekdays[2]
    case '水':
      return weekdays[3]
    case '木':
      return weekdays[4]
    case '金':
      return weekdays[5]
    case '土':
      return weekdays[6]
    default:
      return weekday
  }
}
const TOOLBAR_HEIGHT = 44
const BODY_HEIGHT = 276
const CONTENT_HEIGHT = TOOLBAR_HEIGHT + BODY_HEIGHT

function scrollTo(targetY: number, duration: number, callback?: () => void) {
  // HACK: 最上位要素ではなくBaseLayoutがoveflowしているので、element指定でスクロールさせる
  const baseLayoutElement = document.getElementsByClassName('BaseLayout')[0]
  const startY = baseLayoutElement?.scrollTop ?? 0
  const distance = targetY - startY
  const startTime = performance.now()
  function scrollStep(currentTime: number) {
    const timeElapsed = currentTime - startTime
    const progress = Math.min(timeElapsed / duration, 1)

    if (baseLayoutElement) {
      baseLayoutElement.scrollTop = startY + distance * progress
    }
    if (progress < 1) requestAnimationFrame(scrollStep)
    else {
      if (callback) callback()
    }
  }
  requestAnimationFrame(scrollStep)
}

const EventCalendar = ({ dates, onFetch, month, className }: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isInitializing, setIsInitializing] = useState(false)
  // 日付ごとのコンポーネントの位置
  const [datePositionList, setDatePositionList] = useState<number[]>([])
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())

  const datePositionListRef = useRef(datePositionList)
  const selectedDateRef = useRef(selectedDate)
  const datesRef = useRef(dates)

  const scrollPaddingTop = 16

  const baseLayoutElement = document.getElementsByClassName('BaseLayout')[0]

  const updateMonth = (offset: number) => {
    if (isInitializing) return
    const newMonth = new Date(currentMonth)
    newMonth.setMonth(currentMonth.getMonth() + offset, 1)
    onFetch(dayjs(newMonth).format('YYYYMM'))
  }

  const handlePreviousMonth = () => updateMonth(-1)
  const handleNextMonth = () => updateMonth(1)

  const renderDay = ({ day, ...pickersDayProps }: PickersDayProps<unknown>) => {
    const typedDay = day as Date
    const typedPickersDayProps = pickersDayProps as PickersDayProps<Date>
    const formattedDate = dayjs(typedDay).format('YYYY/MM/DD')
    const isHighlighted = dates.includes(formattedDate)
    const selected = dayjs(typedDay).isSame(
      dayjs(selectedDateRef.current),
      'day'
    )
    const defaultStyle = {
      fontSize: '16px!important',
    }
    const selectedStyle = {
      '&&:focus': {
        backgroundColor: 'primary.main',
        color: 'white',
      },
    }
    const sx = selected ? { ...defaultStyle, ...selectedStyle } : defaultStyle
    const DefaultDay = (
      <PickersDay
        {...typedPickersDayProps}
        day={typedDay}
        selected={selected}
        disabled={!isHighlighted}
        today={false}
        sx={sx}
      />
    )

    return (
      <Box sx={{ position: 'relative' }}>
        {DefaultDay}
        {isHighlighted && !selected && (
          <Box
            sx={{
              position: 'absolute',
              top: '27px',
              left: 'calc(50% - 1px)',
              maxWidth: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
            }}
          />
        )}
      </Box>
    )
  }

  const handleScroll = () => {
    const positions = datePositionListRef.current
    const dates = datesRef.current
    if (!positions.length) return
    const position = baseLayoutElement?.scrollTop ?? 0
    const findLastIndex = (
      arr: number[],
      predicate: (value: number, index: number, array: number[]) => boolean
    ) => {
      for (let i = arr.length - 1; i >= 0; i--) {
        if (predicate(arr[i], i, arr)) {
          return i
        }
      }
      return -1
    }
    const index = findLastIndex(
      positions,
      pos => pos <= position - scrollPaddingTop
    )
    if (index >= 0 && dates[index]) {
      setSelectedDate(new Date(dates[index]))
      setCurrentMonth(new Date(dates[index]))
    }
  }

  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleNextMonth,
    onSwipedRight: handlePreviousMonth,
  })

  useEffect(() => {
    datePositionListRef.current = datePositionList
    selectedDateRef.current = selectedDate
    datesRef.current = dates
  }, [datePositionList, selectedDate, dates])

  useEffect(() => {
    baseLayoutElement?.addEventListener('scroll', handleScroll)
    return () => {
      baseLayoutElement?.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (dates.length) {
      setIsInitializing(true)
      setTimeout(() => {
        const dateElements = document.getElementsByClassName(className)
        const positions = Array.from(dateElements).map(
          element => element.getBoundingClientRect()?.top ?? 0
        )
        const normalizedPositions =
          positions?.map(pos => pos - positions[0]) ?? []
        setDatePositionList(normalizedPositions)
        const today = dayjs().format('YYYY/MM/DD')
        const index = datesRef.current
          ?.map(date => dayjs(date))
          .findIndex(
            date =>
              (date.isSame(today, 'month') && date.isSame(today, 'day')) ||
              date.isAfter(today, 'day')
          )
        const scrollIndex = index >= 0 ? index : 0
        scrollTo(
          normalizedPositions[scrollIndex] + scrollPaddingTop,
          300,
          () => {
            setSelectedDate(dayjs(datesRef.current[scrollIndex]).toDate())
            setCurrentMonth(dayjs(datesRef.current[scrollIndex]).toDate())
          }
        )
        setIsInitializing(false)
      }, 1500) // レンダリングが完了しないとpositionを取得できないためバッファを設定する
    }
  }, [dates.length])

  useEffect(() => {
    setCurrentMonth(dayjs(month).toDate())
  }, [month])

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 600,
          position: 'fixed',
          top: 56,
          zIndex: 9,
          pt: styles.safeAreaTop,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            height: 44,
            backgroundColor: 'white',
            padding: '0 20px',
          }}
        >
          <Icons.PrevArrow
            fontSize="xSmall"
            onClick={handlePreviousMonth}
            sx={{ cursor: 'pointer' }}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
            onClick={() => {
              setIsOpen(!isOpen)
              const index =
                dates?.findIndex(
                  date => date === dayjs(selectedDate).format('YYYY/MM/DD')
                ) ?? null
              if (index !== null) {
                scrollTo(datePositionList[index] + scrollPaddingTop, 300)
              }
            }}
          >
            <Typography variant="h6" sx={{ cursor: 'pointer', pr: 1 }}>
              {dayjs(currentMonth).format('M月')}
            </Typography>
            {isOpen ? (
              <Icons.UpArrow fontSize="small" sx={{ cursor: 'pointer' }} />
            ) : (
              <Icons.DownArrow fontSize="small" sx={{ cursor: 'pointer' }} />
            )}
          </Box>
          <Icons.NextArrow
            fontSize="xSmall"
            onClick={handleNextMonth}
            sx={{ cursor: 'pointer' }}
          />
        </Box>
        {isOpen && (
          <Box {...swipeHandlers}>
            <StyledDatePicker
              displayStaticWrapperAs="desktop"
              value={currentMonth ? dayjs(currentMonth) : null}
              onChange={value => {
                if (value && dayjs.isDayjs(value)) {
                  const index =
                    dates?.findIndex(
                      date => date === dayjs(value).format('YYYY/MM/DD')
                    ) ?? null
                  const dateElements =
                    document.getElementsByClassName(className)
                  const positions = Array.from(dateElements).map(
                    element => element.getBoundingClientRect()?.top ?? 0
                  )
                  const normalizedPositions =
                    positions?.map(pos => pos - positions[0]) ?? []
                  setDatePositionList(normalizedPositions)
                  if (index !== null) {
                    scrollTo(
                      normalizedPositions[index] + scrollPaddingTop,
                      300,
                      () => {
                        setSelectedDate(value.toDate())
                        setCurrentMonth(value.toDate())
                      }
                    )
                  }
                }
              }}
              dayOfWeekFormatter={day => convertToFullWeekday(day)}
              slots={{ day: renderDay }}
            />
          </Box>
        )}
      </Box>
      <Box height={isOpen ? CONTENT_HEIGHT : TOOLBAR_HEIGHT} />
    </Box>
  )
}

export default EventCalendar
